import {
  Box,
  ButtonBase,
  Chip,
  InputAdornment,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material"
import { alpha, useTheme } from "@mui/material/styles"
import { LICENCE_SECTIONS, LICENCE_SECTION_LABELS, type LicenceSection } from "@xop/dashboard-shared"
import { CheckCircle2, Puzzle, Rocket, Search, XCircle } from "lucide-react"
import { useMemo, useState, type JSX } from "react"

/**
 * The ONE way features are picked, anywhere in the CRM (2026-09-15).
 *
 * The licence builder and the Edit Agreement modal both ask the same question
 * — which of the product's features does this customer have? — and until
 * today they answered it with two different controls: a sectioned tile grid
 * in the builder, a flat checkbox grid with "6006"-style codes in the
 * agreement. This is the tile grid, extracted so both read alike: All /
 * Selected / Not selected with counts, a search by name, one card per section
 * with its icon and count, and a tile per feature — a green tick when it is
 * on, a grey cross when it is not.
 *
 * EXACTLY TWO SECTIONS, in a fixed order (CEO, 2026-09-15): "Value Added
 * Services", then "Platform Extensions" — the way the C1 appliance's own
 * licence page lays them out. The caller decides which section each item sits
 * in (`licenceSectionOf(category)` from shared); this only lays them out and
 * never invents a third heading.
 *
 * Each tile is a `role="checkbox"` button named by the feature, so it reads
 * and tabs like the checkbox it replaced. A raw code is never printed here —
 * the caption is the caller's (a part number and a price, or the reason a
 * tile cannot be picked).
 */

export type FeatureTileFilter = "all" | "selected" | "unselected"

export interface FeatureTileItem {
  /** What `onToggle` reports. Unique across the list. */
  id: string
  name: string
  section: LicenceSection
  /** The line under the name — a part number and a price, or why the tile
   * cannot be picked. */
  caption: string
  on: boolean
  /** False: greyed, `aria-disabled`, never toggles (a Pricing row nobody has
   * linked to an appliance feature yet). */
  selectable: boolean
  /** Greyed but still toggleable — a legacy key a licence already names. */
  muted?: boolean
  retired?: boolean
  /** Matched by the search box besides the name (a raw key, say). */
  searchText?: string
  testId?: string
}

export interface FeatureTilePickerProps {
  items: FeatureTileItem[]
  disabled: boolean
  onToggle: (id: string) => void
  /** A sentence under each section heading. */
  hints?: Partial<Record<LicenceSection, string>>
  /** `data-testid` prefix of each section card: `<prefix>-<section>`. */
  groupTestIdPrefix?: string
  searchPlaceholder?: string
}

const SECTION_ICONS: Record<LicenceSection, typeof Rocket> = {
  value_added_services: Rocket,
  platform_extensions: Puzzle,
}

/** The two sections, in the appliance's order, each with the items that sit
 * under it; an empty section is omitted. */
export function featureTileSections<T extends { section: LicenceSection }>(
  items: readonly T[],
): Array<{ section: LicenceSection; label: string; items: T[] }> {
  return LICENCE_SECTIONS.flatMap((section) => {
    const rows = items.filter((item) => item.section === section)
    return rows.length > 0 ? [{ section, label: LICENCE_SECTION_LABELS[section], items: rows }] : []
  })
}

export function FeatureTilePicker({
  items,
  disabled,
  onToggle,
  hints = {},
  groupTestIdPrefix = "feature-group",
  searchPlaceholder = "Search features by name…",
}: FeatureTilePickerProps): JSX.Element {
  const theme = useTheme()
  const [filter, setFilter] = useState<FeatureTileFilter>("all")
  const [query, setQuery] = useState("")

  const selectedCount = useMemo(() => items.filter((item) => item.on).length, [items])
  const needle = query.trim().toLowerCase()

  const groups = featureTileSections(
    items.filter((item) => {
      if (filter === "selected" && !item.on) return false
      if (filter === "unselected" && item.on) return false
      if (!needle) return true
      return item.name.toLowerCase().includes(needle) || (item.searchText ?? "").toLowerCase().includes(needle)
    }),
  )

  const accent = (section: LicenceSection): string =>
    section === "value_added_services" ? theme.palette.primary.main : theme.palette.info.main

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={filter}
          onChange={(_event, next: FeatureTileFilter | null) => {
            if (next) setFilter(next)
          }}
          aria-label="Show features"
        >
          {(
            [
              ["all", `All (${items.length})`],
              ["selected", `Selected (${selectedCount})`],
              ["unselected", `Not selected (${items.length - selectedCount})`],
            ] as const
          ).map(([value, label]) => (
            <ToggleButton
              key={value}
              value={value}
              sx={{ px: 2, fontSize: 12, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase" }}
            >
              {label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <TextField
          size="small"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            // The picker sits inside a form; Enter while searching must not submit it.
            if (event.key === "Enter") event.preventDefault()
          }}
          placeholder={searchPlaceholder}
          inputProps={{ "aria-label": "Search features by name" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={16} />
              </InputAdornment>
            ),
          }}
          sx={{ flex: "1 1 260px", maxWidth: 420 }}
        />
      </Stack>

      {groups.map((group) => {
        const Icon = SECTION_ICONS[group.section]
        const colour = accent(group.section)
        const hint = hints[group.section]
        return (
          <Box
            key={group.section}
            data-testid={`${groupTestIdPrefix}-${group.section}`}
            sx={{ border: 1, borderColor: "divider", borderRadius: 2, p: { xs: 2, md: 3 }, bgcolor: "background.paper" }}
          >
            <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 1.5,
                  display: "grid",
                  placeItems: "center",
                  color: colour,
                  bgcolor: alpha(colour, 0.12),
                }}
              >
                <Icon size={16} />
              </Box>
              <Typography sx={{ fontSize: 17, fontWeight: 600 }}>{group.label}</Typography>
              <Chip size="small" label={group.items.length} sx={{ fontWeight: 600 }} />
              {hint ? (
                <Typography variant="caption" color="text.secondary">
                  {hint}
                </Typography>
              ) : null}
            </Stack>
            <Box
              sx={{
                display: "grid",
                gap: 1.5,
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" },
              }}
            >
              {group.items.map((item) => (
                <FeatureTile key={item.id} item={item} disabled={disabled} onToggle={onToggle} />
              ))}
            </Box>
          </Box>
        )
      })}

      {groups.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
          No features match the current filter.
        </Typography>
      ) : null}
    </Stack>
  )
}

function FeatureTile({
  item,
  disabled,
  onToggle,
}: {
  item: FeatureTileItem
  disabled: boolean
  onToggle: (id: string) => void
}): JSX.Element {
  const { on, selectable } = item
  const greyed = !selectable || item.muted === true
  return (
    <ButtonBase
      role="checkbox"
      aria-checked={on}
      aria-label={item.name}
      aria-disabled={!selectable || undefined}
      disabled={disabled || !selectable}
      onClick={() => onToggle(item.id)}
      {...(item.testId ? { "data-testid": item.testId } : {})}
      sx={(theme) => ({
        width: "100%",
        justifyContent: "flex-start",
        alignItems: "flex-start",
        textAlign: "left",
        gap: 1.5,
        p: 1.75,
        borderRadius: 1.5,
        border: 1,
        borderColor: on && !greyed ? alpha(theme.palette.success.main, 0.45) : theme.palette.divider,
        bgcolor: on && !greyed ? alpha(theme.palette.success.main, 0.07) : greyed ? "surfaceSoft" : "background.paper",
        opacity: greyed ? 0.6 : 1,
        transition: "border-color 120ms, background-color 120ms",
        "&:hover": { borderColor: on ? theme.palette.success.main : theme.palette.text.disabled },
        "&.Mui-focusVisible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
        "&.Mui-disabled": { opacity: greyed ? 0.6 : 0.75 },
      })}
    >
      <Box sx={{ color: on ? (greyed ? "text.disabled" : "success.main") : "text.disabled", display: "flex", mt: "1px" }}>
        {on ? <CheckCircle2 size={20} /> : <XCircle size={20} />}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography sx={{ fontSize: 14, fontWeight: on ? 600 : 500, color: "text.primary" }}>{item.name}</Typography>
          {item.retired ? <Chip label="Retired" size="small" variant="outlined" /> : null}
        </Stack>
        {item.caption ? (
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.25 }}>
            {item.caption}
          </Typography>
        ) : null}
      </Box>
    </ButtonBase>
  )
}
